import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaSync,
  FaPlay,
  FaChevronDown,
  FaChevronRight,
  FaClock,
  FaTerminal,
  FaFlask,
  FaTimes,
  FaHistory,
} from 'react-icons/fa';
import { getTests, runTests, getTestRun, getTestRuns } from '../api/client';

const TestRunner = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSuites, setExpandedSuites] = useState({});
  const [activeRunId, setActiveRunId] = useState(null);
  const [activeRunData, setActiveRunData] = useState(null);
  const [resultView, setResultView] = useState(null); // run_id to view full results
  const [resultData, setResultData] = useState(null);
  const [resultLoading, setResultLoading] = useState(false);
  const [runHistory, setRunHistory] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showOutput, setShowOutput] = useState(false);
  const pollRef = useRef(null);

  const fetchTests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getTests();
      setData(result);
      // If there's an active run, start polling
      if (result.active_run && result.active_run.status === 'running') {
        setActiveRunId(result.active_run.run_id);
        setActiveRunData(result.active_run);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have access to the test runner.');
      } else {
        setError('Failed to load tests.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  // Poll active run
  useEffect(() => {
    if (!activeRunId) return;
    const poll = async () => {
      try {
        const run = await getTestRun(activeRunId);
        setActiveRunData(run);
        if (run.status !== 'running') {
          setActiveRunId(null);
          // Refresh test list to get updated last_run info
          fetchTests();
        }
      } catch {
        // ignore polling errors
      }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => clearInterval(pollRef.current);
  }, [activeRunId, fetchTests]);

  const handleRun = async (suite, testFiles = null) => {
    try {
      const result = await runTests({ suite, test_files: testFiles });
      setActiveRunId(result.run_id);
      setActiveRunData({ run_id: result.run_id, status: 'running', suite, output: '' });
    } catch (err) {
      if (err.response?.status === 409) {
        setError('Tests are already running. Please wait for completion.');
        setTimeout(() => setError(''), 5000);
      } else {
        setError(err.response?.data?.detail || 'Failed to start tests.');
        setTimeout(() => setError(''), 5000);
      }
    }
  };

  const handleViewResult = async (runId) => {
    setResultView(runId);
    setResultLoading(true);
    setShowOutput(false);
    try {
      const result = await getTestRun(runId);
      setResultData(result);
    } catch {
      setResultData(null);
    } finally {
      setResultLoading(false);
    }
  };

  const handleViewHistory = async () => {
    setShowHistory(true);
    try {
      const result = await getTestRuns();
      setRunHistory(result.runs || []);
    } catch {
      setRunHistory([]);
    }
  };

  const toggleSuite = (suiteId) => {
    setExpandedSuites(prev => ({ ...prev, [suiteId]: !prev[suiteId] }));
  };

  const getOutcomeBadge = (outcome) => {
    if (!outcome) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-500">Not run</span>;
    if (outcome === 'passed') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Passed</span>;
    if (outcome === 'failed') return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Failed</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">{outcome}</span>;
  };

  const formatDuration = (secs) => {
    if (!secs) return '-';
    if (secs < 1) return `${Math.round(secs * 1000)}ms`;
    if (secs < 60) return `${secs.toFixed(1)}s`;
    return `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`;
  };

  const formatTime = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleString();
  };

  const formatTimeAgo = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary-500" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-3" />
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  const suites = data?.suites || {};

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <FaFlask className="text-white text-xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-800">Test Runner</h1>
              <p className="text-neutral-600">Run and monitor automated tests</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleViewHistory}
              className="flex items-center gap-2 px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors text-sm font-medium"
            >
              <FaHistory />
              Run History
            </button>
            <button
              onClick={() => fetchTests()}
              className="flex items-center gap-2 px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors text-sm font-medium"
            >
              <FaSync />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && data && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <FaExclamationTriangle className="text-red-500 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Active Run Banner */}
      {activeRunData && activeRunData.status === 'running' && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-3">
            <FaSpinner className="animate-spin text-blue-500 text-lg" />
            <span className="font-semibold text-blue-800">Tests Running...</span>
            <span className="text-sm text-blue-600 ml-auto">{activeRunData.suite} suite</span>
          </div>
          {activeRunData.output && (
            <pre className="bg-neutral-900 text-green-400 text-xs p-4 rounded-lg max-h-48 overflow-y-auto font-mono whitespace-pre-wrap">
              {activeRunData.output.slice(-3000)}
            </pre>
          )}
        </div>
      )}

      {/* Active Run Completed Banner */}
      {activeRunData && activeRunData.status !== 'running' && activeRunData.summary && (
        <div className={`mb-6 ${activeRunData.status === 'passed' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border rounded-xl p-5`}>
          <div className="flex items-center gap-3">
            {activeRunData.status === 'passed' ? (
              <FaCheckCircle className="text-green-500 text-xl" />
            ) : (
              <FaTimesCircle className="text-red-500 text-xl" />
            )}
            <span className={`font-semibold ${activeRunData.status === 'passed' ? 'text-green-800' : 'text-red-800'}`}>
              Tests {activeRunData.status === 'passed' ? 'Passed' : 'Failed'}
            </span>
            <span className="text-sm ml-2 text-neutral-600">
              {activeRunData.summary.passed} passed, {activeRunData.summary.failed} failed
              {activeRunData.summary.errors > 0 && `, ${activeRunData.summary.errors} errors`}
            </span>
            <button
              onClick={() => handleViewResult(activeRunData.run_id)}
              className="ml-auto text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              View Details
            </button>
            <button onClick={() => setActiveRunData(null)} className="text-neutral-400 hover:text-neutral-600">
              <FaTimes />
            </button>
          </div>
        </div>
      )}

      {/* Suite Cards */}
      <div className="space-y-4">
        {Object.entries(suites).map(([suiteId, suite]) => {
          const isExpanded = expandedSuites[suiteId] !== false; // default expanded
          const testFiles = suite.test_files || [];
          const hasResults = testFiles.some(tf => tf.last_run);
          const allPassed = hasResults && testFiles.every(tf => !tf.last_run || tf.last_run.outcome === 'passed');
          const anyFailed = testFiles.some(tf => tf.last_run?.outcome === 'failed');
          const isRunning = activeRunData?.status === 'running' && activeRunData?.suite === suiteId;
          const isUnavailable = suite.available === false;
          const canRun = !activeRunId && !isUnavailable;

          return (
            <div key={suiteId} className={`bg-white rounded-xl shadow-md border overflow-hidden ${isUnavailable ? 'border-neutral-200 opacity-75' : 'border-neutral-200'}`}>
              {/* Suite Header */}
              <div
                className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-neutral-50 transition-colors"
                onClick={() => toggleSuite(suiteId)}
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? <FaChevronDown className="text-neutral-400 text-sm" /> : <FaChevronRight className="text-neutral-400 text-sm" />}
                  <div>
                    <h3 className="font-semibold text-neutral-800">{suite.name}</h3>
                    <p className="text-xs text-neutral-500">
                      {testFiles.length} test files &middot; {suite.runner}
                      {isUnavailable && <span className="ml-2 text-amber-600 font-medium">&middot; Runtime not available</span>}
                    </p>
                  </div>
                  {!isUnavailable && hasResults && (
                    <span className={`ml-3 ${anyFailed ? 'text-red-500' : allPassed ? 'text-green-500' : 'text-neutral-400'}`}>
                      {anyFailed ? <FaTimesCircle /> : allPassed ? <FaCheckCircle /> : null}
                    </span>
                  )}
                </div>
                {isUnavailable ? (
                  <span className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-amber-50 text-amber-600 border border-amber-200" title={suite.unavailable_reason}>
                    <FaExclamationTriangle className="text-xs" />
                    Unavailable
                  </span>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRun(suiteId);
                    }}
                    disabled={!canRun}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      !canRun
                        ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                        : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm'
                    }`}
                  >
                    {isRunning ? <FaSpinner className="animate-spin" /> : <FaPlay className="text-xs" />}
                    Run Suite
                  </button>
                )}
              </div>

              {/* Unavailable notice */}
              {isExpanded && isUnavailable && (
                <div className="px-6 py-4 bg-amber-50 border-t border-amber-100">
                  <p className="text-sm text-amber-700">{suite.unavailable_reason}</p>
                </div>
              )}

              {/* Test Files List */}
              {isExpanded && (
                <div className="border-t border-neutral-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-neutral-50">
                        <th className="text-left px-6 py-2.5 font-medium text-neutral-600">Test File</th>
                        <th className="text-center px-4 py-2.5 font-medium text-neutral-600 w-24">Result</th>
                        <th className="text-center px-4 py-2.5 font-medium text-neutral-600 w-28">Pass/Fail</th>
                        <th className="text-center px-4 py-2.5 font-medium text-neutral-600 w-24">Duration</th>
                        <th className="text-right px-4 py-2.5 font-medium text-neutral-600 w-36">Last Run</th>
                        <th className="text-center px-6 py-2.5 font-medium text-neutral-600 w-24">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testFiles.map((tf) => {
                        const lr = tf.last_run;
                        return (
                          <tr key={tf.name} className="border-t border-neutral-100 hover:bg-neutral-50 transition-colors">
                            <td className="px-6 py-3">
                              <span className="font-mono text-neutral-700">{tf.name}</span>
                            </td>
                            <td className="px-4 py-3 text-center">{getOutcomeBadge(lr?.outcome)}</td>
                            <td className="px-4 py-3 text-center text-xs">
                              {lr ? (
                                <span>
                                  <span className="text-green-600 font-medium">{lr.passed}</span>
                                  {' / '}
                                  <span className="text-red-600 font-medium">{lr.failed + (lr.errors || 0)}</span>
                                </span>
                              ) : '-'}
                            </td>
                            <td className="px-4 py-3 text-center text-xs text-neutral-500">
                              {formatDuration(lr?.duration)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              {lr ? (
                                <div>
                                  <span className="text-xs text-neutral-400" title={formatTime(lr.timestamp)}>
                                    {formatTimeAgo(lr.timestamp)}
                                  </span>
                                  {lr.run_id && (
                                    <button
                                      onClick={() => handleViewResult(lr.run_id)}
                                      className="ml-2 text-blue-500 hover:text-blue-700 text-xs"
                                      title="View full results"
                                    >
                                      details
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-xs text-neutral-400">Never</span>
                              )}
                            </td>
                            <td className="px-6 py-3 text-center">
                              {isUnavailable ? (
                                <span className="text-xs text-neutral-400">-</span>
                              ) : (
                                <button
                                  onClick={() => handleRun(suiteId, [tf.name])}
                                  disabled={!canRun}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                                    !canRun
                                      ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                                      : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                  }`}
                                  title={`Run ${tf.name}`}
                                >
                                  <FaPlay className="inline text-[10px]" /> Run
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Result Detail Modal */}
      {resultView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => { setResultView(null); setResultData(null); }}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 flex-shrink-0">
              <div>
                <h2 className="text-lg font-bold text-neutral-800">Test Run Results</h2>
                {resultData && (
                  <p className="text-xs text-neutral-500">
                    {resultData.suite_name} &middot; {formatTime(resultData.started_at)}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3">
                {resultData?.output && (
                  <button
                    onClick={() => setShowOutput(!showOutput)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      showOutput ? 'bg-neutral-800 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    <FaTerminal className="text-[10px]" />
                    {showOutput ? 'Hide Output' : 'Full Output'}
                  </button>
                )}
                <button onClick={() => { setResultView(null); setResultData(null); }} className="p-2 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100">
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {resultLoading ? (
                <div className="flex items-center justify-center h-40">
                  <FaSpinner className="animate-spin text-3xl text-primary-500" />
                </div>
              ) : resultData ? (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                    <div className={`rounded-lg p-3 text-center ${resultData.status === 'passed' ? 'bg-green-50' : 'bg-red-50'}`}>
                      <p className={`text-2xl font-bold ${resultData.status === 'passed' ? 'text-green-600' : 'text-red-600'}`}>
                        {resultData.status === 'passed' ? 'PASS' : 'FAIL'}
                      </p>
                      <p className="text-xs text-neutral-500">Overall</p>
                    </div>
                    <div className="bg-neutral-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-neutral-700">{resultData.summary?.total || 0}</p>
                      <p className="text-xs text-neutral-500">Total</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-600">{resultData.summary?.passed || 0}</p>
                      <p className="text-xs text-neutral-500">Passed</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-600">{resultData.summary?.failed || 0}</p>
                      <p className="text-xs text-neutral-500">Failed</p>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-yellow-600">{resultData.summary?.errors || 0}</p>
                      <p className="text-xs text-neutral-500">Errors</p>
                    </div>
                  </div>

                  {/* Full output toggle */}
                  {showOutput && resultData.output && (
                    <div className="mb-6">
                      <pre className="bg-neutral-900 text-green-400 text-xs p-4 rounded-lg max-h-80 overflow-y-auto font-mono whitespace-pre-wrap">
                        {resultData.output}
                      </pre>
                    </div>
                  )}

                  {/* Per-file results */}
                  <div className="space-y-3">
                    {(resultData.tests || []).map((tf) => (
                      <TestFileResult key={tf.file} fileResult={tf} formatDuration={formatDuration} />
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-neutral-500 text-center py-8">Failed to load results.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Run History Modal */}
      {showHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowHistory(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 flex-shrink-0">
              <h2 className="text-lg font-bold text-neutral-800">Run History</h2>
              <button onClick={() => setShowHistory(false)} className="p-2 text-neutral-400 hover:text-neutral-600 rounded-lg hover:bg-neutral-100">
                <FaTimes />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {!runHistory ? (
                <div className="flex items-center justify-center h-20">
                  <FaSpinner className="animate-spin text-2xl text-primary-500" />
                </div>
              ) : runHistory.length === 0 ? (
                <p className="text-neutral-500 text-center py-8">No test runs found.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200">
                      <th className="text-left py-2 font-medium text-neutral-600">Suite</th>
                      <th className="text-center py-2 font-medium text-neutral-600">Result</th>
                      <th className="text-center py-2 font-medium text-neutral-600">Tests</th>
                      <th className="text-right py-2 font-medium text-neutral-600">When</th>
                      <th className="text-center py-2 font-medium text-neutral-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {runHistory.map((run) => (
                      <tr key={run.run_id} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="py-2.5 font-medium text-neutral-700">{run.suite_name || run.suite}</td>
                        <td className="py-2.5 text-center">{getOutcomeBadge(run.status)}</td>
                        <td className="py-2.5 text-center text-xs">
                          <span className="text-green-600">{run.summary?.passed || 0}</span>
                          {' / '}
                          <span className="text-red-600">{run.summary?.failed || 0}</span>
                        </td>
                        <td className="py-2.5 text-right text-xs text-neutral-500" title={formatTime(run.finished_at)}>
                          {formatTimeAgo(run.finished_at || run.started_at)}
                        </td>
                        <td className="py-2.5 text-center">
                          <button
                            onClick={() => { setShowHistory(false); handleViewResult(run.run_id); }}
                            className="text-blue-500 hover:text-blue-700 text-xs font-medium"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-component for individual test file results with expandable test list
const TestFileResult = ({ fileResult, formatDuration }) => {
  const [expanded, setExpanded] = useState(fileResult.outcome === 'failed');

  return (
    <div className={`border rounded-lg overflow-hidden ${fileResult.outcome === 'failed' ? 'border-red-200' : 'border-neutral-200'}`}>
      <div
        className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${
          fileResult.outcome === 'failed' ? 'bg-red-50 hover:bg-red-100' : 'bg-neutral-50 hover:bg-neutral-100'
        }`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <FaChevronDown className="text-neutral-400 text-xs" /> : <FaChevronRight className="text-neutral-400 text-xs" />}
          {fileResult.outcome === 'passed' ? (
            <FaCheckCircle className="text-green-500" />
          ) : (
            <FaTimesCircle className="text-red-500" />
          )}
          <span className="font-mono text-sm font-medium text-neutral-700">{fileResult.file}</span>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <span className="text-green-600 font-medium">{fileResult.passed} passed</span>
          {fileResult.failed > 0 && <span className="text-red-600 font-medium">{fileResult.failed} failed</span>}
          {fileResult.errors > 0 && <span className="text-yellow-600 font-medium">{fileResult.errors} errors</span>}
          <span className="text-neutral-400 flex items-center gap-1">
            <FaClock className="text-[10px]" />
            {formatDuration(fileResult.duration)}
          </span>
        </div>
      </div>
      {expanded && fileResult.tests && (
        <div className="divide-y divide-neutral-100">
          {fileResult.tests.map((t, idx) => (
            <div key={idx} className="px-4 py-2.5 pl-12">
              <div className="flex items-center gap-2">
                {t.outcome === 'passed' ? (
                  <FaCheckCircle className="text-green-400 text-xs flex-shrink-0" />
                ) : (
                  <FaTimesCircle className="text-red-400 text-xs flex-shrink-0" />
                )}
                <span className="text-sm text-neutral-700 font-mono">{t.name}</span>
                <span className="text-xs text-neutral-400 ml-auto">{formatDuration(t.duration)}</span>
              </div>
              {t.message && (
                <pre className="mt-2 text-xs bg-red-50 text-red-800 p-3 rounded overflow-x-auto whitespace-pre-wrap font-mono ml-5">
                  {t.message}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TestRunner;
